/**
 * One age rule for the whole club (#127, decision D7).
 *
 * A member is a JUNIOR for a membership year when they are 16 or under on
 * 1 January of that year (that is, not yet 17), and they stay junior for the
 * whole year. Used for membership, pricing, check-in and competition
 * divisions, from date of birth, never from a stored flag.
 *
 * Imported by the frontend as `@shared/membership` and by Lambdas as
 * `../../../shared/membership`, like `shared/fees.ts`.
 *
 * Which year to pass:
 *   - registration, grouping, requests and approval: `clubYear(now)`
 *   - renewal: the year being renewed
 *   - competitions: the event's own year (so past events keep their rule)
 */

export type AgeCategory = 'JUNIOR' | 'ADULT';

/** The calendar year in club time (Australia/Sydney), not UTC. */
export function clubYear(now: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric' }).format(now));
}

/** The club year of an ISO instant (e.g. an event's start), or null. */
export function clubYearOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : clubYear(d);
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})/;

/** `YYYY-MM-DD` from a date-of-birth string, or null when it can't be read. */
function normaliseDob(dob: string | null | undefined): string | null {
  if (!dob || typeof dob !== 'string') return null;
  const m = YMD.exec(dob.trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/**
 * True when someone born on `dob` is a junior for `membershipYear`: 16 or under
 * on 1 January (not yet 17), i.e. born after 1 January of (year − 17).
 * Compared as `YYYY-MM-DD` strings, so no time zone can shift it.
 * For 2026: born 1 Jan 2009 or earlier = adult; born 2 Jan 2009 or later = junior.
 * An unreadable date of birth is treated as adult (the safe default for the
 * adult-only checks; callers that require a DOB validate it separately).
 */
export function isJuniorForYear(dob: string | null | undefined, membershipYear: number): boolean {
  const d = normaliseDob(dob);
  if (!d || !Number.isFinite(membershipYear)) return false;
  return d > `${membershipYear - 17}-01-01`;
}

export function ageCategoryForYear(dob: string | null | undefined, membershipYear: number): AgeCategory {
  return isJuniorForYear(dob, membershipYear) ? 'JUNIOR' : 'ADULT';
}

/** Family role stored on Member rows ('ADULT' | 'CHILD') for a non-primary. */
export function familyRoleForYear(dob: string | null | undefined, membershipYear: number): 'ADULT' | 'CHILD' {
  return isJuniorForYear(dob, membershipYear) ? 'CHILD' : 'ADULT';
}

/** The first date of birth that is junior for `membershipYear` (e.g. '2009-01-02' for 2026), for copy and validation messages. */
export function earliestJuniorDob(membershipYear: number): string {
  return `${membershipYear - 17}-01-02`;
}
